import {
  Button,
  ButtonGroup,
  DataStatus,
  Form,
  hideNotification,
  showNotification,
} from 'platform/components';
import {HStack, Text, VStack} from 'platform/foundation';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useLazyCreateDataQueryQuery} from '@dms/api/core';
import {
  GetSupplierOrderVariantsForServiceOrderResponse,
  useGetSupplierOrderVariantsForServiceOrderQuery,
  useGetSupplierOrderVariantsQuery,
  usePostServiceOrderSupplierOrdersMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {handleApiError, useGetSupplierCustomersQuery, WAREHOUSE_GRID_CODES} from '@dms/shared';

import {ApiError, composePath, getRandomId, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {convertToSearchParams} from 'features/eag-grid';

import {refreshServiceOrderGrids} from '../../../../utils/refreshServiceOrderGrids';
import {CreateSupplierOrdersButton} from './CreateSupplierOrdersButton';
import {
  createSupplierOrdersFormSchema,
  CreateSupplierOrdersFormValues,
} from './createSupplierOrdersFormSchema';
import {SelectAllWarehousesButton} from './SelectAllWarehousesButton';
import {WarehouseSupplierOrderCard} from './WarehouseSupplierOrderCard';

const FIRST_ROW_INDEX = '1';
// Twice the default notification time, so the user has time to notice the link.
const CREATED_ORDERS_LINK_AUTO_CLOSE_MS = 10_000;

const getSupplierOrderListUrl = (dataQueryId: string) =>
  composePath(warehouseRoutes.supplierOrderList, {
    queryParams: convertToSearchParams(
      {
        dataGrids: {
          gridCode: WAREHOUSE_GRID_CODES.warehouseSupplierOrders,
          queryId: dataQueryId,
          rowIndex: FIRST_ROW_INDEX,
        },
      },
      true
    ),
    isSearchParamsPreserved: false,
  });

const getDefaultValues = (
  warehouses: GetSupplierOrderVariantsForServiceOrderResponse
): CreateSupplierOrdersFormValues => ({
  warehouses: warehouses.map((warehouse) => {
    // A single variant is the only choice, so it is preselected and locked.
    const singleOrderVariantId =
      warehouse.supplierOrderVariants.length === 1
        ? warehouse.supplierOrderVariants[0].supplierOrderVariantId
        : null;

    return {
      warehouseId: warehouse.warehouseId,
      isSelected: true,
      orderVariantId: singleOrderVariantId,
      supplierId: null,
      orderType: null,
      supplierAccount: null,
      isOrderTypeRequired: false,
      isSupplierAccountRequired: false,
    };
  }),
});

interface CreateSupplierOrdersDialogProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  onClose: () => void;
}

export function CreateSupplierOrdersDialog(props: CreateSupplierOrdersDialogProps) {
  const [postServiceOrderSupplierOrders] = usePostServiceOrderSupplierOrdersMutation();
  const [createDataQuery] = useLazyCreateDataQueryQuery();

  const warehousesQuery = useGetSupplierOrderVariantsForServiceOrderQuery(
    {serviceOrderId: props.serviceOrderId},
    {refetchOnMountOrArgChange: true}
  );
  // The service order endpoint lacks the integration flag that decides if the order type is required.
  const supplierOrderVariantsQuery = useGetSupplierOrderVariantsQuery({});
  const suppliersQuery = useGetSupplierCustomersQuery();

  const isLoading =
    warehousesQuery.isLoading || supplierOrderVariantsQuery.isLoading || suppliersQuery.isLoading;
  const isError =
    warehousesQuery.isError || supplierOrderVariantsQuery.isError || suppliersQuery.isError;

  const createSupplierOrdersDataQuery = (supplierOrderIds: string[]) =>
    createDataQuery({
      code: WAREHOUSE_GRID_CODES.warehouseSupplierOrders,
      createDataQueryRequestBody: {
        filters: {supplierOrderId: supplierOrderIds},
        order: [],
        smartSearch: null,
      },
    });

  const handleSubmit = (values: CreateSupplierOrdersFormValues) => {
    // A browser opens a new tab only for a few seconds after the user's click. Creating several
    // supplier orders can take longer, so the tab opens now and gets its address when the orders exist.
    const createdSupplierOrdersTab = window.open('about:blank', '_blank');
    const successNotificationId = getRandomId();

    const handleError = (error: ApiError) => {
      createdSupplierOrdersTab?.close();
      handleApiError(error);
    };

    return postServiceOrderSupplierOrders({
      serviceOrderId: props.serviceOrderId,
      serviceCaseId: props.serviceCaseId,
      body: {
        supplierOrders: values.warehouses
          .filter((warehouse) => warehouse.isSelected)
          .map((warehouse) => ({
            orderVariantId: warehouse.orderVariantId ?? '',
            supplierId: warehouse.supplierId ?? '',
            orderType: warehouse.orderType,
            supplierAccount: warehouse.supplierAccount,
          })),
      },
    })
      .unwrap()
      .then((response) => {
        // The orders exist from here on, so the user learns it even if opening them fails.
        showNotification.success(i18n.t('page.warehouse.notification.supplierOrderCreated'), {
          id: successNotificationId,
        });
        props.onClose();
        // The items grids are not RTK-driven, so the invalidated tags do not show the new item states.
        refreshServiceOrderGrids();

        return createSupplierOrdersDataQuery(response.rows.map((row) => row.orderId))
          .unwrap()
          .then((dataQuery) => {
            const supplierOrderListUrl = getSupplierOrderListUrl(dataQuery.dataQueryId);

            // The browser blocked the tab, so the user opens the orders from a link. The first
            // notification may already be closed, so the link comes in a new one.
            if (isNil(createdSupplierOrdersTab)) {
              const linkNotificationId = getRandomId();

              hideNotification(successNotificationId);
              showNotification.success(i18n.t('page.warehouse.notification.supplierOrderCreated'), {
                id: linkNotificationId,
                autoClose: CREATED_ORDERS_LINK_AUTO_CLOSE_MS,
                hyperlinks: [
                  {
                    title: i18n.t('entity.order.actions.openCreatedSupplierOrders'),
                    href: supplierOrderListUrl,
                    target: '_blank',
                    onClick: () => hideNotification(linkNotificationId),
                  },
                ],
              });
              return;
            }

            createdSupplierOrdersTab.location.href = supplierOrderListUrl;
            createdSupplierOrdersTab.focus();
          })
          .catch(handleError);
      })
      .catch(handleError);
  };

  return (
    <DataStatus
      data={warehousesQuery.data}
      isLoading={isLoading}
      isError={isError}
      isEmpty={isNilOrEmpty(warehousesQuery.data)}
      minHeight={40}
      data-testid={suffixTestId('status', props)}
    >
      {({data: warehouses}) => (
        <Form<CreateSupplierOrdersFormValues>
          experimentalZodSchema={createSupplierOrdersFormSchema}
          defaultValues={getDefaultValues(warehouses)}
          onSubmit={handleSubmit}
        >
          {(control, formApi) => (
            <VStack spacing={4}>
              <HStack justify="space-between" align="center" spacing={4}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.order.labels.createSupplierOrdersDescription')}
                </Text>
                <SelectAllWarehousesButton
                  control={control}
                  onToggle={(isSelected) =>
                    warehouses.forEach((_, index) =>
                      formApi.setValue(`warehouses.${index}.isSelected`, isSelected)
                    )
                  }
                  data-testid={suffixTestId('selectAll', props)}
                />
              </HStack>
              <VStack spacing={2}>
                {warehouses.map((warehouse, index) => (
                  <WarehouseSupplierOrderCard
                    key={warehouse.warehouseId}
                    index={index}
                    warehouse={warehouse}
                    suppliers={suppliersQuery.data}
                    supplierOrderVariants={supplierOrderVariantsQuery.data}
                    control={control}
                    formApi={formApi}
                    data-testid={suffixTestId(`warehouse-${warehouse.warehouseId}`, props)}
                  />
                ))}
              </VStack>
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  title={i18n.t('general.actions.discard')}
                  onClick={props.onClose}
                  data-testid={suffixTestId('discard', props)}
                />
                <CreateSupplierOrdersButton
                  control={control}
                  data-testid={suffixTestId('create', props)}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      )}
    </DataStatus>
  );
}
